import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import http from 'helpers/requests';
import { cgToast, notifyError } from 'modules/Common/ui/toast';
import { STALE_TIME, CACHE_TIME } from 'hooks/usePageDataPrefetcher';
import { useTickOnRecordCreated } from 'modules/GuidingChecklist/hooks/useTickOnRecordCreated';
import i18n from 'i18n';
import { offerImportAssignment } from 'modules/Common/stores/assignImportsOfferStore';

export const useSearchInstallations = ({ searchForm = {}, pagination }) => {
  return useQuery({
    queryKey: ['installations', ...Object.values(searchForm), pagination.page, pagination.limit],
    queryFn: async () => {
      const response = await http.post('/installations/search', {
        ...searchForm,
        page: pagination.page,
        items: pagination.limit,
      });
      return {
        installations: response.data.installations,
        itemCount: response.data.itemCount,
      };
    },
    // Align with prefetcher so cached data from hover is treated as fresh
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
  });
};

export const useCountries = () => {
  return useQuery({
    queryKey: ['countries'],
    queryFn: async () => {
      const response = await http.get('/country/all');
      return response.data;
    },
  });
};

/**
 * `silent` suppresses the success toast for callers that report the outcome themselves.
 * The shared table deletion flow does: it reads the {deleted, blocked, notFound} envelope and
 * can say "3 deleted, 2 blocked", where this toast can only ever say "deleted 3" — which is a
 * lie when rows were refused. Standalone pages keep the toast.
 */
export const useDeleteInstallation = ({ onCrossOrgError, silent = false } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ installationId }) => {
      if (Array.isArray(installationId)) {
        return await http.remove(`/installations`, {
          ids: installationId,
        });
      } else {
        return await http.remove(`/installations/${installationId}`);
      }
    },
    onMutate: async ({ installationId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(['installations']);

      // Snapshot the previous value
      const previousInstallations = queryClient.getQueryData(['installations']);

      // Optimistically update to the new value
      queryClient.setQueryData(['installations'], (old) => {
        if (!old) return old;
        return {
          ...old,
          installations: old.installations.filter((installation) =>
            Array.isArray(installationId)
              ? !installationId.includes(installation._id)
              : installation._id !== installationId,
          ),
          itemCount: old.itemCount - (Array.isArray(installationId) ? installationId.length : 1),
        };
      });

      // Return a context object with the snapshotted value
      return { previousInstallations };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousInstallations) {
        queryClient.setQueryData(['installations'], context.previousInstallations);
      }
      const errorData = err?.response?.data;
      if (err?.response?.status === 409 && errorData?.code === 'CROSS_ORG_USAGE') {
        onCrossOrgError?.(errorData.orgNames, errorData.entity ?? errorData.entityType);
      } else {
        cgToast.apiError(err);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we're in sync with server
      queryClient.invalidateQueries(['installations']);
      queryClient.invalidateQueries(['installations-stats']);
    },
    onSuccess: (res) => {
      if (silent) return;
      let deletedCount = res.data.deletedCount;
      cgToast.success(i18n.t('common:toasts.success', 'Success'), {
        description: i18n.t('installations:toasts.deleted', {
          count: deletedCount ?? 1,
          defaultValue: 'Deleted {{count}} installation',
          defaultValue_other: 'Deleted {{count}} installations',
        }),
      });
    },
  });
};

export const useImportInstallations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ validData }) => {
      await http.post('/installations/import', {
        excelInstallations: validData,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['installations']);
      queryClient.invalidateQueries(['installations-stats']);
      cgToast.success(i18n.t('common:toasts.success', 'Success'), {
        description: i18n.t(
          'installations:toasts.imported',
          'Installations have been imported successfully',
        ),
      });
    },
    // D11: no toast here — the rejection reaches the import wizard's
    // translated general-error banner (ImportWizard.jsx apiErrorMessage).
  });
};

export const useImportEmissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ validData }) => {
      const res = await http.post('/installations/emissions', {
        emissions: validData,
      });
      return res?.data;
    },
    onSuccess: (data) => {
      // Every host of the classic import and the Upload Hub's sheet import comes
      // through here (backend docs/features/import-installation-backfill, D14).
      offerImportAssignment(data?.emissionsFactors);
      queryClient.invalidateQueries(['installations']);
      queryClient.invalidateQueries(['installations-stats']);
      cgToast.success(i18n.t('common:toasts.success', 'Success'), {
        description: i18n.t(
          'installations:toasts.emissionsImported',
          'Emissions data has been imported successfully',
        ),
      });
    },
    // D11: no toast here — the rejection reaches the import wizard's
    // translated general-error banner (ImportWizard.jsx apiErrorMessage).
  });
};

export const useCreateInstallation = ({ silent = false } = {}) => {
  const queryClient = useQueryClient();
  // Same gap as suppliers: the step auto-ticks off the bulk import only, and
  // the Upload hub's EU template flow creates installations through here.
  const tickUploadInstallations = useTickOnRecordCreated('upload-installations');

  return useMutation({
    mutationFn: async (data) => {
      const response = await http.post('/installations/add', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['installations']);
      queryClient.invalidateQueries(['installations-stats']);
      tickUploadInstallations();
      cgToast.success(i18n.t('common:toasts.success', 'Success'), {
        description: i18n.t(
          'installations:toasts.created',
          'Installation has been created successfully',
        ),
      });
    },
    // D11: callers that render the failure inline pass { silent: true }.
    onError: silent ? undefined : notifyError,
  });
};

export const useInstallation = (installationId) => {
  return useQuery({
    queryKey: ['installation', installationId],
    queryFn: async () => {
      if (!installationId) return null;
      const response = await http.get(`/installations/${installationId}`);
      return response.data;
    },
    enabled: !!installationId,
  });
};

export const useUpdateInstallation = ({ silent = false } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ installationId, data }) => {
      const response = await http.put(`/installations/${installationId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['installations']);
      queryClient.invalidateQueries(['installations-stats']);
      // Single-installation cache too — the details page reads
      // ['installation', id] and must reflect the edit immediately.
      queryClient.invalidateQueries(['installation']);
      cgToast.success(i18n.t('common:toasts.success', 'Success'), {
        description: i18n.t(
          'installations:toasts.updated',
          'Installation has been updated successfully',
        ),
      });
    },
    onError: silent ? undefined : notifyError,
  });
};
